// Single place that connects an accident case to the Crane / Recovery / Legal flows:
//   • SERVICE_TARGET   – which on-spot service types have a real flow, and which one
//   • craneDefaults    – case  → prefill for CreatecraneServiceModal
//   • legalDefaults    – case  → prefill for CreateLegalCaseModal
//   • findLinked       – find the active linked request for a service type
//   • linkedRoute      – URL of a linked request's detail page

// ── ADJUST: detail-page URL for a legal case. ────────────────────────────────
// AdvocateCaseDetailPage reads useParams().caseId, so the route ends in /:caseId.
// Put your real path here (check your router file).
const LEGAL_DETAIL_ROUTE = (code) => `/admin/quick-services/legal-cases/${code}`;
const QUICK_DETAIL_ROUTE = (code) => `/admin/quick-services/${code}`;

// Quick-service validation is stricter than the case form: starts with 6-9.
const PHONE_RE = /^[6-9]\d{9}$/;

// useCaseDetail gives accidentTime as 12-hour ("02:30 pm"); <input type="time"> needs
// 24-hour "HH:MM". Prefer the raw timestamp; accept an already-24h value
// (what CreateCaseModal's success screen passes).
const time24 = (c) => {
  if (c?.accidentDateTime) {
    return new Date(c.accidentDateTime).toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  }
  return /^\d{2}:\d{2}$/.test(c?.accidentTime || "") ? c.accidentTime : "";
};

// Keys must match SERVICE_CATALOG types in ServiceHelper.jsx exactly.
// ON_SPOT_MECHANIC is intentionally absent: it has no flow yet, so it stays a
// manual "pending" service.
export const SERVICE_TARGET = {
  CRANE:      { kind: "quick", serviceType: "crane" },
  TOWING_VAN: { kind: "quick", serviceType: "recovery" },
  ADVOCATE:   { kind: "legal" },
};

// c   = caseInfo   (from useCaseDetail, or the success payload in CreateCaseModal)
// acc = accidentDetails
export const craneDefaults = (c, acc) => ({
  vehicle_number:    c.vehicleNo || "",
  pickup_location:   c.location || "",
  owner_name:        c.customerName || "",
  // Drop numbers that would fail the crane form's validation; the admin re-enters them.
  owner_contact:     PHONE_RE.test(c.customerPhone || "") ? c.customerPhone : "",
  issue_description: acc?.description || "",
});

export const legalDefaults = (c, acc) => ({
  vehicleNumber:    c.vehicleNo || "",
  customerName:     c.customerName || "",
  customerPhone:    c.customerPhone || "",
  customerEmail:    c.customerEmail || "",
  incidentLocation: c.location || "",
  accidentDate:     c.accidentDate || "",
  accidentTime:     time24(c),
  caseDescription:  acc?.description || "",
});

// linkedServices is newest-first (the backend orders by id DESC).
// Cancelled quick-service requests are ignored so a new one can be created.
export const findLinked = (catalogType, linkedServices = []) => {
  const target = SERVICE_TARGET[catalogType];
  if (!target) return null;

  const matches = linkedServices.filter((l) =>
    target.kind === "legal"
      ? l.kind === "legal"
      : l.kind === "quick" && l.serviceType === target.serviceType
  );
  return matches.find((l) => l.status !== "CANCELLED") || null;
};

export const linkedRoute = (l) =>
  l.kind === "quick" ? QUICK_DETAIL_ROUTE(l.code) : LEGAL_DETAIL_ROUTE(l.code);